import { useId, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { depDescarcareCu, descarcaExercitiiPdf, type DepDescarcare } from '../utils/descarca-exercitii-pdf';

interface ButonDescarcaExercitiiProps {
  lessonId: string;
  titlu: string;
  pdfManualUrl: string;
  variant?: 'link' | 'outline';
  className?: string;
  disabled?: boolean;
  /** Textul butonului; implicit „Descarcă exerciții PDF”. */
  eticheta?: string;
  /**
   * Alta sursa pentru PDF decat ruta autentificata. O trece pagina publica a colaboratorului, care
   * n-are voie sa atinga `/api/lessons/**` (un 401 acolo ar declansa deconectarea).
   */
  cere?: DepDescarcare['cere'];
}

/**
 * B3 (Task 15): „Descarcă exerciții PDF" — pe card (toate tipurile de lectie cu PDF) si pe „Detalii".
 * Cheama helper-ul SINCRON in click (ramura „tab nou" trebuie sa ramana in gestul omului, altfel Safari
 * o blocheaza). Daca descarcarea prin backend esueaza (404, retea), NU deschidem un pop-up dupa `await`:
 * aratam langa buton un link „Deschide PDF-ul", pe care omul il apasa el.
 */
export function ButonDescarcaExercitii({
  lessonId,
  titlu,
  pdfManualUrl,
  variant = 'link',
  className,
  disabled = false,
  eticheta = 'Descarcă exerciții PDF',
  cere,
}: ButonDescarcaExercitiiProps) {
  const [seDescarca, setSeDescarca] = useState(false);
  const [esuat, setEsuat] = useState(false);
  const idEroare = useId();

  const descarca = () => {
    if (seDescarca) return;
    setEsuat(false);
    setSeDescarca(true);
    void descarcaExercitiiPdf(lessonId, titlu, pdfManualUrl, cere ? depDescarcareCu(cere) : undefined)
      .then((rezultat) => setEsuat(rezultat === 'esuat'))
      .finally(() => setSeDescarca(false));
  };

  return (
    <>
      <Button
        variant={variant}
        className={cn(variant === 'link' && 'justify-start', className)}
        onClick={descarca}
        disabled={disabled || seDescarca}
        aria-busy={seDescarca || undefined}
        aria-describedby={esuat ? idEroare : undefined}
        title="Descarcă exercițiile în format PDF"
      >
        <Download aria-hidden="true" />
        <span className="text-wrap">{seDescarca ? 'Se descarcă…' : eticheta}</span>
      </Button>
      {esuat && (
        <p id={idEroare} role="alert" className="text-sm text-pretty text-error">
          PDF-ul nu a putut fi descărcat.{' '}
          <a
            href={pdfManualUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="touch-target-link inline-flex items-center font-medium underline underline-offset-2"
          >
            Deschide PDF-ul
          </a>
        </p>
      )}
    </>
  );
}
